/**
 * Shared read model for the Fridge.com Appliance Price Index.
 *
 * Single source of truth for both the LLM content component
 * (components/seo/data-portal-llm-content.tsx) and the schema component
 * (components/seo/data-schemas.tsx), so the sr-only prose, the JSON-LD Dataset,
 * and the on-page tables all cite the SAME self-computed numbers from
 * app/data/_data/price-index.json (produced by the P12 index pipeline).
 *
 * INTEGRITY: every number here is read directly from that JSON. Nothing is
 * invented, rounded into a headline, or narrated. A category-month with fewer
 * than the publication minimum matched models (n < publication_min_n) is carried
 * as below-threshold and never presented as a published figure.
 */
import indexData from '@/app/data/_data/price-index.json'

export interface SeriesRow {
  series: string
  category: string
  month: string
  index: number | null
  n: number
  median_price_cents: number | null
  below_publication_threshold: boolean
}

export interface DataMeta {
  methodology_version: string
  computed_at: string
  base_month: string | null
  floor_month: string | null
  target_month: string | null
  sources: Record<string, string>
  gates: {
    price_cents_range?: [number, number]
    relative_range?: [number, number]
    min_daily_obs_per_model_month?: number
    publication_min_n?: number
    imputation?: string
    seasonal_adjustment?: string
  }
  limitations: string[]
  per_category_n: Record<string, number>
}

/** Latest publishable observation for one category, plus its anchor context. */
export interface CategorySummary {
  category: string
  label: string
  /** Most recent month with n >= publication threshold (the headline figure). */
  latestMonth: string | null
  latestIndex: number | null
  latestN: number
  latestMedianCents: number | null
  /** Number of months in the window that clear the publication threshold. */
  publishableMonths: number
  totalMonths: number
  /** True when at least one month clears the threshold. */
  published: boolean
  /** Distinct matched models available for the category (meta.per_category_n). */
  matchedModels: number
}

export const dataMeta = indexData.meta as unknown as DataMeta
export const seriesRows = indexData.series as unknown as SeriesRow[]

/** Human labels for the catalog category slugs the index tracks. */
export const CATEGORY_LABELS: Record<string, string> = {
  'all-refrigerators': 'All refrigerators',
  'all-freezers': 'All freezers',
  'all-specialty': 'All specialty refrigeration',
  'all-commercial': 'All commercial refrigeration',
  'french-door': 'French-door refrigerators',
  'side-by-side': 'Side-by-side refrigerators',
  'top-freezer': 'Top-freezer refrigerators',
  'bottom-freezer': 'Bottom-freezer refrigerators',
  'counter-depth': 'Counter-depth refrigerators',
  'compact': 'Compact refrigerators',
  'refrigerator': 'Standard refrigerators',
  'chest-freezer': 'Chest freezers',
  'upright-freezer': 'Upright freezers',
  'compact-freezer': 'Compact freezers',
  'wine-cooler': 'Wine coolers',
  'beverage-cooler': 'Beverage coolers',
  'kegerator': 'Kegerators',
  'ice-maker': 'Ice makers',
}

export function categoryLabel(slug: string): string {
  return (
    CATEGORY_LABELS[slug] ??
    slug
      .split('-')
      .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
      .join(' ')
  )
}

/** 'YYYY-MM' → 'June 2026'. Parsed as UTC to avoid timezone drift. */
export function formatMonthLabel(month: string | null | undefined): string {
  if (!month) return 'n/a'
  const [y, m] = month.split('-').map(Number)
  if (!y || !m) return month
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

export function formatDollars(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—'
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/**
 * Group the flat series into per-category summaries. The headline figure for a
 * category is its most recent month that clears the publication threshold;
 * categories that never clear it are returned with published=false and no
 * headline number (they are disclosed, never fabricated).
 */
export function buildCategorySummaries(): CategorySummary[] {
  const byCategory = new Map<string, SeriesRow[]>()
  for (const row of seriesRows) {
    const arr = byCategory.get(row.category) ?? []
    arr.push(row)
    byCategory.set(row.category, arr)
  }

  const summaries: CategorySummary[] = []
  for (const [category, rows] of byCategory) {
    const sorted = [...rows].sort((a, b) => a.month.localeCompare(b.month))
    const publishable = sorted.filter((r) => !r.below_publication_threshold)
    const latest = publishable.length > 0 ? publishable[publishable.length - 1] : null
    summaries.push({
      category,
      label: categoryLabel(category),
      latestMonth: latest?.month ?? null,
      latestIndex: latest?.index ?? null,
      latestN: latest?.n ?? 0,
      latestMedianCents: latest?.median_price_cents ?? null,
      publishableMonths: publishable.length,
      totalMonths: sorted.length,
      published: latest !== null,
      matchedModels: dataMeta.per_category_n?.[category] ?? 0,
    })
  }

  // Aggregate ("all-*") categories first, then A→Z by label.
  return summaries.sort((a, b) => {
    const aAll = a.category.startsWith('all-')
    const bAll = b.category.startsWith('all-')
    if (aAll !== bAll) return aAll ? -1 : 1
    return a.label.localeCompare(b.label)
  })
}

export function publishedSummaries(): CategorySummary[] {
  return buildCategorySummaries().filter((s) => s.published)
}

/**
 * Ticker read model for the sitewide chrome data strip (chrome-upgrade lane
 * zone 2). Deliberately derived from buildCategorySummaries() — the SAME
 * loader the /data surfaces read — so the header ticker can never diverge from
 * the published Appliance Price Index figure. Returns the all-refrigerators
 * rollup's latest published reading plus its matched-model ("fridges tracked")
 * count, or null when that category is not currently published (the strip then
 * self-hides). Nothing here is invented or rounded into a headline beyond the
 * one-decimal display of the index the tables already show.
 */
export interface PriceIndexTickerReading {
  category: string
  /** Raw latest published index for all-refrigerators (e.g. 99.83). */
  index: number
  /** One-decimal display string (e.g. "99.8"). */
  indexLabel: string
  /** 'YYYY-MM' of the reading. */
  month: string
  /** Human month label (e.g. "June 2026"). */
  monthLabel: string
  /** Matched models tracked for the category (meta.per_category_n). */
  matchedModels: number
}

export function priceIndexTickerReading(): PriceIndexTickerReading | null {
  const s = buildCategorySummaries().find(
    (c) => c.category === 'all-refrigerators' && c.published
  )
  if (!s || s.latestIndex === null || !s.latestMonth) return null
  return {
    category: s.category,
    index: s.latestIndex,
    indexLabel: s.latestIndex.toFixed(1),
    month: s.latestMonth,
    monthLabel: formatMonthLabel(s.latestMonth),
    matchedModels: s.matchedModels,
  }
}

export function belowThresholdSummaries(): CategorySummary[] {
  return buildCategorySummaries().filter((s) => !s.published)
}

export const publicationMinN = dataMeta.gates?.publication_min_n ?? 30

/**
 * Public display label for the methodology version, e.g. "v1.0".
 *
 * The JSON meta carries the exact pipeline version string (e.g. "1.0.0-draft");
 * that raw value is an internal pipeline marker and is never rendered publicly.
 * Public surfaces show the released major.minor only. The underlying JSON meta
 * is never modified.
 */
export const methodologyVersionLabel: string = (() => {
  const raw = dataMeta.methodology_version ?? ''
  const core = raw.split('-')[0]
  const parts = core.split('.').filter(Boolean)
  if (parts.length >= 2) return `v${parts[0]}.${parts[1]}`
  return core ? `v${core}` : 'n/a'
})()

/** Distinct calendar months present in this release, across all series. */
export const monthsCoveredCount = new Set(seriesRows.map((r) => r.month)).size

/** Total category-month rows in this release (matches the CSV row count). */
export const totalSeriesRowCount = seriesRows.length

/** All tracked categories: published plus disclosed-below-threshold. */
export const trackedCategoryCount = new Set(seriesRows.map((r) => r.category)).size

// ─────────────────────────────────────────────────────────────────────────
// Figure read models (item 143 original-imagery leg).
//
// The render-time SVG figures on the data surfaces derive their numbers HERE,
// from the same JSON every table and schema reads, so a chart can never drift
// from the text beside it (the item-157 single-loader principle). Nothing is
// smoothed, imputed, or invented: a figure only ever plots published rows.
// ─────────────────────────────────────────────────────────────────────────

/** Brand-palette line colors for the aggregate rollup trend chart. */
export const AGGREGATE_TREND_COLORS: Record<string, string> = {
  'all-refrigerators': '#0469f0', // Fridge primary blue
  'all-freezers': '#ff9951', // Fridge primary orange
  'all-specialty': '#004aad', // deep blue
  'all-commercial': '#10a65c', // tone-healthy green
}

export interface TrendPoint {
  /** 'YYYY-MM' — sortable key, formatted for display by the chart. */
  month: string
  index: number
}

export interface TrendSeries {
  category: string
  label: string
  color: string
  points: TrendPoint[]
}

/**
 * The four aggregate rollup index trends (all-refrigerators, all-freezers,
 * all-specialty, all-commercial) as month-sorted published points — the data
 * behind the /data/price-index trend chart. Only rows that clear the
 * publication threshold and carry an index value are plotted.
 */
export function aggregateTrendSeries(): TrendSeries[] {
  const out: TrendSeries[] = []
  for (const category of Object.keys(AGGREGATE_TREND_COLORS)) {
    const points = seriesRows
      .filter(
        (r) =>
          r.series === 'new_condition' &&
          r.category === category &&
          !r.below_publication_threshold &&
          r.index !== null
      )
      .sort((a, b) => a.month.localeCompare(b.month))
      .map((r) => ({ month: r.month, index: r.index as number }))
    if (points.length >= 2) {
      out.push({
        category,
        label: categoryLabel(category),
        color: AGGREGATE_TREND_COLORS[category],
        points,
      })
    }
  }
  return out
}

/** One category's full published index trend — a single-series chart feed. */
export function categoryTrendSeries(category: string, color: string): TrendSeries | null {
  const points = seriesRows
    .filter(
      (r) =>
        r.series === 'new_condition' &&
        r.category === category &&
        !r.below_publication_threshold &&
        r.index !== null
    )
    .sort((a, b) => a.month.localeCompare(b.month))
    .map((r) => ({ month: r.month, index: r.index as number }))
  if (points.length < 2) return null
  return { category, label: categoryLabel(category), color, points }
}

/**
 * One published category's full index trend as a small-multiples panel — the
 * feed for the per-type sparkline grid on /data/price-index. Every category with
 * at least two published (n ≥ threshold) monthly readings gets a panel; the
 * aggregate "all-*" rollups sort first, then the primary types A→Z (the
 * buildCategorySummaries order). Nothing is smoothed or imputed — a panel plots
 * only the published points, exactly as the tables do.
 */
export interface SmallMultiplePanel {
  category: string
  label: string
  points: TrendPoint[]
  latestIndex: number
  latestMonth: string
  firstIndex: number
  firstMonth: string
  /** True for the four aggregate rollups (all-refrigerators, …). */
  isRollup: boolean
}

export function smallMultipleTrends(): SmallMultiplePanel[] {
  const order = buildCategorySummaries().map((s) => s.category)
  const out: SmallMultiplePanel[] = []
  for (const category of order) {
    const points = seriesRows
      .filter(
        (r) =>
          r.series === 'new_condition' &&
          r.category === category &&
          !r.below_publication_threshold &&
          r.index !== null
      )
      .sort((a, b) => a.month.localeCompare(b.month))
      .map((r) => ({ month: r.month, index: r.index as number }))
    if (points.length < 2) continue
    out.push({
      category,
      label: categoryLabel(category),
      points,
      latestIndex: points[points.length - 1].index,
      latestMonth: points[points.length - 1].month,
      firstIndex: points[0].index,
      firstMonth: points[0].month,
      isRollup: category.startsWith('all-'),
    })
  }
  return out
}

/** Shared index band [min, max] across every small-multiple panel, so the grid
 *  can be drawn on ONE comparable scale (rounded outward to whole index points,
 *  and always containing the base = 100 line). */
export function smallMultipleDomain(): [number, number] {
  const vals: number[] = [100]
  for (const p of smallMultipleTrends()) for (const pt of p.points) vals.push(pt.index)
  const lo = Math.floor(Math.min(...vals))
  const hi = Math.ceil(Math.max(...vals))
  return [lo, hi]
}

/**
 * The two biggest cumulative movers among the PRIMARY (non-rollup) published
 * categories, measured as distance of the latest reading from the base (= 100):
 * the type furthest above base and the one furthest below. Both are honest
 * cumulative moves since each category's anchor month, the same figures the
 * trend chart and tables show. Aggregate rollups are excluded so a mover is
 * always a specific appliance type.
 */
export interface PriceMover {
  category: string
  label: string
  index: number
  /** Signed points from base (index − 100), e.g. +8.4 or −11.2. */
  fromBase: number
  latestMonth: string
}

export function priceIndexMovers(): { up: PriceMover | null; down: PriceMover | null } {
  const primaries = smallMultipleTrends().filter((p) => !p.isRollup)
  if (primaries.length === 0) return { up: null, down: null }
  const toMover = (p: SmallMultiplePanel): PriceMover => ({
    category: p.category,
    label: p.label,
    index: p.latestIndex,
    fromBase: Math.round((p.latestIndex - 100) * 100) / 100,
    latestMonth: p.latestMonth,
  })
  const sorted = [...primaries].sort((a, b) => b.latestIndex - a.latestIndex)
  return { up: toMover(sorted[0]), down: toMover(sorted[sorted.length - 1]) }
}

export interface MedianBar {
  category: string
  label: string
  medianCents: number
  month: string
  n: number
}

/**
 * Latest published median price for each primary (non-rollup) category, sorted
 * high→low — the data behind the /data/price-index median-by-type bar chart.
 * Aggregate "all-*" rollups are excluded so a type is never double-counted;
 * every value here also appears in the page's Latest-published-readings table.
 */
export function latestMedianByType(): MedianBar[] {
  return buildCategorySummaries()
    .filter(
      (s) =>
        s.published &&
        !s.category.startsWith('all-') &&
        s.latestMedianCents !== null &&
        s.latestMonth !== null
    )
    .map((s) => ({
      category: s.category,
      label: s.label,
      medianCents: s.latestMedianCents as number,
      month: s.latestMonth as string,
      n: s.latestN,
    }))
    .sort((a, b) => b.medianCents - a.medianCents)
}

/**
 * Canonical FAQ set for the Appliance Price Index, computed from the live
 * numbers. Shared by the FAQPage JSON-LD (components/seo/data-schemas.tsx) and
 * the sr-only content (components/seo/data-portal-llm-content.tsx) so the
 * structured data always matches the rendered text.
 */
export function buildDataFAQs(): Array<{ question: string; answer: string }> {
  const published = publishedSummaries()
  const below = belowThresholdSummaries()
  const baseLabel = formatMonthLabel(dataMeta.base_month)
  const latestLabel = formatMonthLabel(dataMeta.target_month)
  const computedLabel = formatMonthLabel(dataMeta.computed_at?.slice(0, 7))

  const belowSentence =
    below.length > 0
      ? `${below.length} ${below.length === 1 ? 'category is' : 'categories are'} below that threshold (${below
          .map((s) => s.label.toLowerCase())
          .join(' and ')}) and ${below.length === 1 ? 'is' : 'are'} not published as index figures.`
      : 'Every tracked category currently clears that threshold.'

  return [
    {
      question: 'What is the Fridge.com Appliance Price Index?',
      answer: `It is a matched-model price index for refrigeration appliances published by Fridge.com. It measures how the price of a fixed set of refrigerator and freezer models changes over time, with the base month (${baseLabel}) set to 100. It is computed from the Fridge.com catalog and price history, not from any third-party index.`,
    },
    {
      question: 'What data is the index built from?',
      answer: `The set of models comes from the Fridge.com live catalog of active refrigeration appliances. The prices come from NEW-condition retail price observations collected for those models as daily price history and aggregated by calendar month. A separate retailer price-history series is not part of this version.`,
    },
    {
      question: 'Why use a matched-model index instead of average prices?',
      answer: `An average of whatever is listed each month moves whenever the product mix changes, even if no individual price moved. A matched-model index compares each model only against itself across consecutive months, so a change in catalog composition cannot masquerade as a price change.`,
    },
    {
      question: 'Which categories are published, and which are not?',
      answer: `${published.length} categories clear the publication threshold of at least ${publicationMinN} matched models and carry an index figure. ${belowSentence}`,
    },
    {
      question: 'How current is the index?',
      answer: `This release covers ${baseLabel} through ${latestLabel} and was computed in ${computedLabel} under methodology ${methodologyVersionLabel}. The index is computed by calendar month, and each release records its computation timestamp in the dataset metadata.`,
    },
    {
      question: 'Can I download the data?',
      answer: `Yes. The full series is available as a CSV file at https://fridge.com/data/price-index/csv, and the methodology is documented at https://fridge.com/data/methodology.`,
    },
  ]
}
